import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Injectable } from '@angular/core';

import { Contato } from '../../models/contato';
import { Mensagem } from '../../models/mensagem';

@Injectable()
export class ContatoLocalProvider {

    private _$contatos: BehaviorSubject<Contato[]>;
    private readonly limiteDeMensagens = 50;

    constructor() {
        this._$contatos = new BehaviorSubject<Contato[]>(this.getContatos() || []);
    }

    public get $contatos() {
        return this._$contatos.asObservable();
    }

    public adicionarContato(contato: Contato): void {
        let contatos = this.getContatos();
        if (!contatos) {
            contatos = [];
        }
        contatos.push(contato);
        this.setContatos(contatos);
    }

    public getContatoComIdUsuario(idUsuario: number): any {
        const contatos = this.getContatos();
        if (contatos) {
            return contatos.find(c => c.idUsuario === idUsuario);
        }
    }

    public setContatos(contatos: Contato[]): void {
        localStorage.setItem('contatos', JSON.stringify(contatos));
        this._$contatos.next(contatos);
    }

    public getContatos(): Contato[] {
        return JSON.parse(localStorage.getItem('contatos'));
    }

    public setContatosUsuarios(usuarios: any[]): void {
        this.setContatos(usuarios[0].Contatos);
    }

    public setMensagensContato(idContato: number, mensagens: any): void {
        let contato = this.getContato(idContato);
        contato.mensagens = mensagens;
        this.updateContato(contato);
    }

    public getContato(idContato: number): Contato {
        const contatos = this.getContatos();
        if (contatos) {
           return contatos.find(c => c.idContato === idContato);
        }
    }

    public marcarMensagensLidas(idContato: number): void {
        const contato = this.getContato(idContato);
        if (contato && contato.mensagens) {
            for (const mensagem of contato.mensagens) {
                if (contato.idUsuario === mensagem.idUsuarioOrigem) {
                    mensagem.statusMensagemLida = true;
                }
            }
            this.updateContato(contato);
        }
    }

    public marcarTodasMensagensRecebida(idContato: number): void {
        const contato = this.getContato(idContato);
        if (contato && contato.mensagens) {
            for (const mensagem of contato.mensagens) {
                if (contato.idUsuario === mensagem.idUsuarioOrigem) {
                    mensagem.statusMensagemRecebida = true;
                }
            }
            this.updateContato(contato);
        }
    }

    public marcarMensagemRecebida(idContato: number, idEnvio: number): void {
        const contato = this.getContato(idContato);
        if (contato) {
            for (let i = contato.mensagens.length - 1; i >= 0; i--) {
                const mensagem = contato.mensagens[i];
                if ( mensagem.idEnvio && mensagem.idEnvio === idEnvio) {
                    mensagem.statusMensagemRecebida = true;
                    break;
                }
            }
            this.updateContato(contato);
        }
    }

    public adicionarNovaMensagem(idContato: number, mensagem: Mensagem): void {
        let contato = this.getContato(idContato);
        if (!contato) {
            return
        }
        if (!contato.mensagens) {
            contato.mensagens = [];
        }
        contato.mensagens.push(mensagem);
        this.updateContato(contato);
    }

    public removerMensagensAcimaDoLimite(idContato: number): void {
        const contato = this.getContato(idContato);
        if(contato && contato.mensagens && contato.mensagens.length > this.limiteDeMensagens) {
            contato.mensagens = contato.mensagens.slice(-this.limiteDeMensagens);
            this.updateContato(contato);
        }
    }

    public updateContato(contato: any): void {
        const contatos = this.getContatos();
        for (let i = 0, len = contatos.length; i < len; i++) {
            if (contatos[i].idUsuario === contato.idUsuario) {
                contatos[i] = contato
                break;
            }
        }
        this.setContatos(contatos);
    }

    public getMensagensComRecebimentoPendente(): Mensagem[] {
        const contatos = this.getContatos();
        if (contatos) {
            let mensagensComNotificacaoDeRecebimentoPendente = []
            for (const contato of contatos) {
                for (const mensagem of contato.mensagens) {
                    if (!mensagem.statusMensagemRecebida) {
                        mensagensComNotificacaoDeRecebimentoPendente.push(mensagem);
                    }
                }
            }
            return mensagensComNotificacaoDeRecebimentoPendente;
        }
        return [];
    }

    public getMensagensComEnvioPendente(): Mensagem[] {
        const contatos = this.getContatos();
        if (contatos) {
            const mensagensNaoEnviadas = [];
            for (const contato of contatos){
                for (const mensagem of contato.mensagens) {
                    if (mensagem.pendente) {
                        mensagensNaoEnviadas.push(mensagem);
                    }
                }
            }
            return mensagensNaoEnviadas;
        }
        return [];
    }

    public atualizarMensagem(idContato: number, m: Mensagem): void {
        const contato = this.getContato(idContato);
        if (contato) {
            // a pesquisa é invertida pois é provavel que a mensagem esteja no fim
            for (let i = contato.mensagens.length - 1; i >= 0; i--) {
                const mensagem = contato.mensagens[i];
                if (mensagem.idEnvio && mensagem.idEnvio === m.idEnvio) {
                    contato.mensagens[i] = m
                    break;
                }
            }
            this.updateContato(contato);
        }
    }

    public atualizarMensagemLida(idContato: number, idEnvio: number): void {
        const contato = this.getContato(idContato);
        if (contato && contato.mensagens) {
            // a pesquisa é invertida pois é provavel que a mensagem esteja no fim
            for (let i = contato.mensagens.length - 1; i >= 0; i--) {
                const mensagem = contato.mensagens[i];
                if (mensagem.idEnvio && mensagem.idEnvio === idEnvio) {
                    mensagem.statusMensagemLida = true;
                    break;
                }
            }
        }
    }

    public atualizarMensagemEnviada(idContato: number, idLocalMensagem: number, idEnvio: number): void {
        const contato = this.getContato(idContato);
        if (contato) {
            // a pesquisa é invertida pois é provavel que a mensagem esteja no fim
            for (let i = contato.mensagens.length - 1; i >= 0; i--) {
                const mensagem = contato.mensagens[i];
                if (mensagem.idEnvio && mensagem.idEnvio === idLocalMensagem) {
                    mensagem.idEnvio = idEnvio;
                    mensagem.pendente = false;
                    break;
                }
            }
            this.updateContato(contato);
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/contato-local/contato-local.ts